<template>
  <div class="space-y-4">
    <h2>
      {{ t('bookingSummary') }}
    </h2>
    <div class="space-y-4">
      <div
        v-for="(allocation, index) of filteredAllocations"
        :key="allocation.id"
        class="space-y-2"
      >
        <div v-if="booking.allocations.length > 1" class="flex items-center gap-2 font-medium">
          <div>
            {{ allocation.car.vehicleType.make.name }} {{ allocation.car.vehicleType.model }}
          </div>
          <LicencePlate>{{ allocation.car.licencePlate }}</LicencePlate>
        </div>
        <div v-if="showEdit !== index" class="flex items-stretch gap-2">
          <div class="bg-primary/10 grid flex-1 gap-4 rounded-md bg-white px-4 py-2 md:grid-cols-2">
            <LabeledText :label="t('milage')">
              <div class="flex items-center gap-2">
                {{ allocation.milageStart ? formatFloat(allocation.milageStart) + ' km' : '-' }}
                <ArrowRightIcon class="size-4 shrink-0 text-obsidian-400" />
                {{ allocation.milageEnd ? formatFloat(allocation.milageEnd) + ' km' : '-' }}
                <template v-if="allocation.milageStart && allocation.milageEnd">
                  <EqualIcon class="size-4 shrink-0 text-obsidian-400" />
                  <span>{{ formatFloat(allocation.milageEnd - allocation.milageStart) }} km</span>
                </template>
              </div>
            </LabeledText>

            <LabeledText
              :label="`${carProp(allocation.car, 'fuelType') === FuelType.ELECTRIC ? t('battery') : t('fuelLevel')}`"
            >
              <div class="flex items-center gap-2">
                {{ displayFuelLevel(allocation, 'start') }}
                <ArrowRightIcon class="size-4 shrink-0 text-obsidian-400" />
                {{ displayFuelLevel(allocation, 'end') }}
                <template v-if="allocation.fuelLevelStart && allocation.fuelLevelEnd">
                  <EqualIcon class="size-4 shrink-0 text-obsidian-400" />
                  <span>
                    {{ displayFuelLevelDifference(allocation) }}
                  </span>
                </template>
              </div>
            </LabeledText>

            <LabeledText :label="t('dates')">
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  {{
                    allocation.handoverDate
                      ? formatDate(allocation.handoverDate, getDateTimeFormat())
                      : '-'
                  }}
                  <ArrowRightIcon class="size-4 shrink-0 text-obsidian-400" />
                  {{
                    allocation.returnDate
                      ? formatDate(allocation.returnDate, getDateTimeFormat())
                      : '-'
                  }}
                </div>
                <div
                  v-if="allocation.handoverDate && allocation.returnDate"
                  class="flex items-center gap-2"
                >
                  <EqualIcon class="size-4 shrink-0 text-obsidian-400" />
                  <div>
                    {{ displayDuration(allocation) }}
                  </div>
                </div>
              </div>
            </LabeledText>
            <LabeledText :label="t('involvedHubAgents')">
              <span class="whitespace-pre-line">{{ displayAgents(allocation) }}</span>
            </LabeledText>
          </div>
          <CVButton
            v-if="$can('update', subject('Allocation', allocation), 'milageStart')"
            variant="ghost"
            class="h-auto max-md:px-1.5"
            @click.prevent="showEdit = index"
          >
            <PencilIcon class="size-4" />
          </CVButton>
        </div>
        <BookingSummaryEdit
          v-else
          :allocation="allocation"
          :is-saving="isSaving"
          @save="handleSave"
          @cancel="showEdit = null"
        />
      </div>
    </div>

    <div class="mt-4 flex justify-end">
      <div
        v-if="booking.calculatedAdditionalCosts"
        class="costs-grid grid grid-cols-2 items-end justify-end gap-x-3 gap-y-1 text-right font-medium"
      >
        <p>{{ t('additionalCosts') }}:</p>
        <p
          v-currency="booking.calculatedAdditionalCosts.totalAdditionalCosts"
          class="block text-base font-bold"
        ></p>
        <template v-if="!isLongTerm">
          <p class="text-base">{{ t('finalTotalPrice') }}:</p>
          <p
            v-currency="
              booking.calculatedAdditionalCosts.totalAdditionalCosts +
              booking.priceCalculation.totalPrice
            "
            class="block text-lg font-bold"
          ></p>
        </template>
      </div>
      <div
        v-if="
          (booking.status === BookingStatus.CAR_RETURNED ||
            booking.status === BookingStatus.FINALIZED) &&
          !booking.calculatedAdditionalCosts
        "
        class="text-right text-sm text-obsidian-500"
      >
        {{ t('saveBookingToCalculateAdditionalCosts') }}
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import LabeledText from '../LabeledText.vue';
import { computed, ref } from 'vue';
import { FuelType } from '@/entities/cars/fuel-type.enum';
import { useI18n } from 'vue-i18n';
import { formatFloat } from '@/utils/format-numbers';
import BookingSummaryEdit from './BookingSummaryEdit.vue';
import {
  AllocationStatus,
  BookingStatus,
  type UpdateAllocationDto,
} from '@carvia/ros-client-types';
import type { Allocation } from '@/entities/allocations/allocation.entity.ts';
import { carProp } from '@/utils/car-prop.ts';
import { useUpdateAllocation } from '@/queries/use-allocations';
import { ArrowRightIcon, EqualIcon, PencilIcon } from 'lucide-vue-next';
import CVButton from '../buttons/CVButton.vue';
import { useQueryClient } from '@tanstack/vue-query';
import LicencePlate from '@/components/LicencePlate.vue';
import { subject } from '@casl/ability';
import { getDateTimeFormat } from '@/hooks/use-date-format.ts';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import type { User } from '@/entities/auth/user.entity';
import { DateTime } from 'luxon';
import { isLongTermPricing } from '@/utils/pricing.utils';

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();
const queryClient = useQueryClient();
const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));

const { isPending: isSaving, mutateAsync: updateAllocation } = useUpdateAllocation();

const showEdit = ref<number | null>(null);

const filteredAllocations = computed(() =>
  props.booking.allocations.filter((allocation) =>
    [AllocationStatus.HANDED_OVER, AllocationStatus.RETURNED].includes(allocation.status),
  ),
);

const displayFuelLevel = (allocation: Allocation, mode: 'start' | 'end') => {
  const fuelLevel = allocation[mode === 'start' ? 'fuelLevelStart' : 'fuelLevelEnd'];
  const fuelType = carProp(allocation.car, 'fuelType');
  const batteryCapacityInKwh = carProp(allocation.car, 'batteryCapacityInKwh') ?? 0;
  if (!fuelLevel) return '-';
  return fuelType === FuelType.ELECTRIC
    ? `${Math.round(fuelLevel * 100)}% (${Math.round(batteryCapacityInKwh * fuelLevel)} Kwh)`
    : `${fuelLevel * 8} / 8`;
};

const displayFuelLevelDifference = (allocation: Allocation) => {
  const fuelLevelStart = allocation.fuelLevelStart;
  const fuelLevelEnd = allocation.fuelLevelEnd;
  const fuelType = carProp(allocation.car, 'fuelType');
  const batteryCapacityInKwh = carProp(allocation.car, 'batteryCapacityInKwh') ?? 0;
  const tankVolumeInLiter = carProp(allocation.car, 'tankVolumeInLiter') ?? 0;
  if (!fuelLevelStart || !fuelLevelEnd) return '-';
  const difference = fuelLevelEnd - fuelLevelStart;
  if (fuelType === FuelType.ELECTRIC) {
    const kwhDiff = batteryCapacityInKwh * difference;
    return `${formatFloat(Math.abs(kwhDiff))} Kwh`;
  }
  const literDiff = tankVolumeInLiter * difference;
  return `~ ${formatFloat(Math.abs(literDiff))} l`;
};

const displayAgents = (allocation: Allocation) => {
  const getName = (staffAgent: User | null) =>
    staffAgent ? [staffAgent.firstName, staffAgent.lastName].filter(Boolean).join(' ') : '-';
  const handingOverAgent = getName(allocation.handingOverAgent);
  const returningAgent = getName(allocation.returningAgent);
  return handingOverAgent === returningAgent
    ? handingOverAgent
    : `${t('handover')}: ${handingOverAgent}\n${t('return')}: ${returningAgent}`;
};

const displayDuration = (allocation: Allocation) => {
  if (!allocation.handoverDate || !allocation.returnDate) return '';
  const start = DateTime.fromISO(allocation.handoverDate);
  const end = DateTime.fromISO(allocation.returnDate);
  const dur = end.diff(start, ['days', 'hours', 'minutes']).toObject();
  const dayStr = dur.days ? t('duration.days', { count: dur.days }) : null;
  const hourStr = dur.hours ? t('duration.hours', { count: dur.hours }) : null;
  const minuteStr = dur.minutes ? t('duration.minutes', { count: Math.round(dur.minutes) }) : null;
  return [dayStr, hourStr, minuteStr].filter(Boolean).join(' ').trim();
};

const handleSave = async (allocationId: string, formValues: UpdateAllocationDto) => {
  await updateAllocation({
    id: allocationId,
    allocation: {
      milageStart: formValues.milageStart ?? undefined,
      milageEnd: formValues.milageEnd ?? undefined,
      fuelLevelStart: formValues.fuelLevelStart ?? undefined,
      fuelLevelEnd: formValues.fuelLevelEnd ?? undefined,
    },
  });
  await queryClient.invalidateQueries({ queryKey: ['bookings', props.booking.id] });
  showEdit.value = null;
};
</script>

<i18n lang="json">
{
  "en": {
    "bookingSummary": "Booking Summary",
    "handoverDate": "Handover Date",
    "returnDate": "Return Date",
    "handingOverAgent": "Handing Over Agent",
    "returningAgent": "Returning Agent",
    "agent": "Agent",
    "handover": "Handover",
    "return": "Return",
    "fuelLevel": "Fuel Level",
    "battery": "Battery",
    "milage": "Milage",
    "dates": "Dates",
    "day": "day",
    "days": "days",
    "hour": "hour",
    "hours": "hours",
    "minute": "minute",
    "minutes": "minutes",
    "lessThanMinute": "Less than a minute",
    "additionalCosts": "Additional Costs",
    "finalTotalPrice": "Final Total Price",
    "edit": "Edit",
    "saveBookingToCalculateAdditionalCosts": "Please save booking to calculate additional costs",
    "involvedHubAgents": "Involved Hub Agents",
    "duration": {
      "days": "{count} day|{count} days",
      "hours": "{count} h",
      "minutes": "{count} min"
    }
  },
  "de": {
    "bookingSummary": "Buchungszusammenfassung",
    "handoverDate": "Übergabedatum",
    "returnDate": "Rückgabedatum",
    "handingOverAgent": "Übergebender Agent",
    "returningAgent": "Zurücknehmender Agent",
    "agent": "Agent",
    "handover": "Übergabe",
    "return": "Rückgabe",
    "fuelLevel": "Tankfüllung",
    "battery": "Ladestand",
    "milage": "Kilometer",
    "dates": "Zeitraum",
    "day": "Tag",
    "days": "Tage",
    "hour": "Stunde",
    "hours": "Stunden",
    "minute": "Minute",
    "minutes": "Minuten",
    "lessThanMinute": "Weniger als eine Minute",
    "additionalCosts": "Zusatzkosten",
    "finalTotalPrice": "Finaler Gesamtpreis",
    "edit": "Bearbeiten",
    "saveBookingToCalculateAdditionalCosts": "Bitte speichere die Buchung um die Zusatzkosten zu berechnen",
    "involvedHubAgents": "Beteiligte Hub Agents",
    "duration": {
      "days": "{count} Tag|{count} Tage",
      "hours": "{count} Std.",
      "minutes": "{count} Min."
    }
  },
  "es": {
    "bookingSummary": "Resumen de la reserva",
    "handoverDate": "Fecha de entrega",
    "returnDate": "Fecha de devolución",
    "handingOverAgent": "Agente que entrega",
    "returningAgent": "Agente que recibe",
    "agent": "Agente",
    "handover": "Entrega",
    "return": "Devolución",
    "fuelLevel": "Nivel de combustible",
    "battery": "Batería",
    "milage": "Kilometraje",
    "dates": "Fechas",
    "day": "día",
    "days": "días",
    "hour": "hora",
    "hours": "horas",
    "minute": "minuto",
    "minutes": "minutos",
    "lessThanMinute": "Menos de un minuto",
    "additionalCosts": "Costes adicionales",
    "finalTotalPrice": "Precio total final",
    "edit": "Editar",
    "saveBookingToCalculateAdditionalCosts": "Guarde la reserva para calcular los costes adicionales",
    "involvedHubAgents": "Agentes del hub implicados",
    "duration": {
      "days": "{count} día|{count} días",
      "hours": "{count} h",
      "minutes": "{count} min"
    }
  }
}
</i18n>

<style scoped>
.costs-grid {
  grid-template-columns: [first] auto [line2] auto [end];
}
</style>
